import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errorText } from "../lib/api";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// The owner's flocks (named groups) + counts, and every flock action. Each action re-reads the counts afterwards.
// `enabled` waits for the stored session to be revalidated (a refresh retires the old token).
export const useFlocks = (enabled = true) => {
  const [data, setData] = useState({ flocks: [], unsorted: 0, total: 0, max: 50, loaded: false, failed: false });

  const load = useCallback(
    () =>
      api
        .get("/mine/flocks")
        .then((r) => setData({ ...r.data, loaded: true, failed: false }))
        .catch(() => setData((d) => ({ ...d, loaded: true, failed: true }))),
    [],
  );
  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  const run = async (call, ok, fail) => {
    try {
      const { data: out } = await call();
      if (ok) toast.success(typeof ok === "function" ? ok(out) : ok);
      await load();
      return out || true;
    } catch (e) {
      toast.error(errorText(e, fail));
      await load();
      return false;
    }
  };

  const create = (name) => run(() => api.post("/mine/flocks", { name }), (f) => `Flock “${f.name}” created`, "Could not create the flock.");
  const rename = (id, name) => run(() => api.patch(`/mine/flocks/${id}`, { name }), "Flock renamed", "Could not rename the flock.");
  const remove = (f) => run(() => api.delete(`/mine/flocks/${f.id}`), `“${f.name}” deleted — its ducks are in Unsorted`, "Could not delete the flock.");
  const move = (duckIds, flockId) =>
    run(() => api.post("/mine/move", { duck_ids: duckIds, flock_id: flockId }), (r) => `Moved ${plural(r.moved, "duck", "ducks")} (${plural(r.families, "family", "families")})`, "Could not move those ducks.");

  const setCover = (id, duckId) =>
    run(() => api.put(`/mine/flocks/${id}/cover`, { duck_id: duckId }), duckId ? "Flock cover set" : "The flock shows its newest duck again", "Could not change the flock cover.");

  const shift = (id, dir) => {
    const ids = data.flocks.map((f) => f.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return Promise.resolve(false);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setData((d) => ({ ...d, flocks: ids.map((x) => d.flocks.find((f) => f.id === x)) }));
    return run(() => api.put("/mine/flocks/order", { ids }), null, "Could not reorder the flocks.");
  };

  return { ...data, load, create, rename, remove, move, shift, setCover };
};
